
import React, { createContext, useContext, useState } from "react";
import { cn } from "@/lib/utils";

// Context to manage sidebar state
type SidebarContextType = {
  isOpen: boolean;
  toggle: () => void;
  close: () => void;
};

const SidebarContext = createContext<SidebarContextType | undefined>(undefined);

export function SidebarProvider({
  children,
  defaultOpen = true,
}: {
  children: React.ReactNode;
  defaultOpen?: boolean;
}) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  const toggle = () => setIsOpen(!isOpen);
  const close = () => setIsOpen(false);

  return (
    <SidebarContext.Provider value={{ isOpen, toggle, close }}>
      {children}
    </SidebarContext.Provider>
  );
}

export function useSidebar() {
  const context = useContext(SidebarContext);
  if (!context) {
    throw new Error("useSidebar must be used within a SidebarProvider");
  }
  return context;
}

export function Sidebar({ children, className }: { children: React.ReactNode; className?: string }) {
  const { isOpen } = useSidebar();

  return (
    <aside
      className={cn(
        "h-screen flex-shrink-0 border-r bg-card transition-all duration-300 ease-in-out",
        isOpen ? "w-64" : "w-16",
        className
      )}
    >
      <div className="flex h-full flex-col">{children}</div>
    </aside>
  );
}

export function SidebarHeader({ children, className }: { children?: React.ReactNode; className?: string }) {
  const { isOpen } = useSidebar();

  return (
    <div
      className={cn(
        "flex h-16 items-center justify-between border-b px-4",
        isOpen ? "px-4" : "px-2",
        className
      )}
    >
      {children}
    </div>
  );
}

export function SidebarTrigger() {
  const { toggle } = useSidebar();

  return (
    <button
      type="button"
      className="inline-flex h-10 w-10 items-center justify-center rounded-md text-foreground"
      onClick={toggle}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="h-6 w-6"
      >
        <line x1="3" y1="12" x2="21" y2="12" />
        <line x1="3" y1="6" x2="21" y2="6" />
        <line x1="3" y1="18" x2="21" y2="18" />
      </svg>
      <span className="sr-only">Toggle Menu</span>
    </button>
  );
}

export function SidebarContent({ children, className }: { children: React.ReactNode; className?: string }) {
  return <div className={cn("flex-1 overflow-auto py-2", className)}>{children}</div>;
}

export function SidebarFooter({ children, className }: { children?: React.ReactNode; className?: string }) {
  return <div className={cn("border-t p-4", className)}>{children}</div>;
}

export function SidebarGroup({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <div className={cn("px-2 py-2", className)}>{children}</div>;
}

export function SidebarGroupLabel({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  const { isOpen } = useSidebar();

  if (!isOpen) return null;

  return (
    <div className={cn("mb-2 px-4 text-xs font-semibold text-muted-foreground", className)}>
      {children}
    </div>
  );
}

export function SidebarGroupContent({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <div className={cn("space-y-1", className)}>{children}</div>;
}

export function SidebarMenu({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <ul className={cn("min-w-0 flex-1", className)}>{children}</ul>;
}

export function SidebarMenuItem({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <li className={cn("min-w-0", className)}>{children}</li>;
}

export function SidebarMenuButton({
  children,
  className,
  asChild,
  ...props
}: {
  children: React.ReactNode;
  className?: string;
  asChild?: boolean;
} & React.ButtonHTMLAttributes<HTMLButtonElement>) {
  const { isOpen } = useSidebar();

  if (asChild) {
    return (
      <div
        className={cn(
          "flex cursor-pointer items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors hover:bg-accent hover:text-accent-foreground",
          className
        )}
      >
        {React.Children.map(children, (child) => {
          if (React.isValidElement(child)) {
            return React.cloneElement(child, {
              ...child.props,
              className: cn(
                "flex w-full items-center gap-2",
                isOpen ? "" : "justify-center",
                child.props.className
              ),
              children: React.Children.map(child.props.children, (innerChild, index) => {
                if (index === 0 || isOpen) {
                  return innerChild;
                }
                return null;
              }),
            });
          }
          return child;
        })}
      </div>
    );
  }

  return (
    <button
      className={cn(
        "flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors hover:bg-accent hover:text-accent-foreground",
        isOpen ? "" : "justify-center",
        className
      )}
      {...props}
    >
      {React.Children.map(children, (child, index) => {
        if (index === 0 || isOpen) {
          return child;
        }
        return null;
      })}
    </button>
  );
}
